"use client";

import { Card, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { HomeHeroBanner } from "@/components/home/HomeHeroBanner";
import { HomeTileGrid } from "@/components/home/HomeTileGrid";
import { PageGuide } from "@/components/guides/PageGuide";
import { AppShell } from "@/components/shell/AppShell";
import { fetchHealth } from "@/lib/api";
import { fetchStrainWorkReport } from "@/lib/api/reports";
import type { HomeQueueCounts } from "@/lib/home/tiles";
import { useT } from "@/lib/i18n/I18nProvider";
import { useSession } from "@/providers/SessionProvider";

const { Paragraph, Text } = Typography;

export default function HomePage() {
  const t = useT();
  const { session, loading: authLoading } = useSession();
  const [queues, setQueues] = useState<HomeQueueCounts>({});
  const [apiOk, setApiOk] = useState<boolean | null>(null);

  useEffect(() => {
    void fetchHealth()
      .then(() => setApiOk(true))
      .catch(() => setApiOk(false));
  }, []);

  useEffect(() => {
    if (authLoading || !session?.authenticated) {
      setQueues({});
      return;
    }
    void fetchStrainWorkReport()
      .then((report) => {
        const next: HomeQueueCounts = {};
        for (const row of report.queues) {
          next[row.key] = row.count;
        }
        setQueues(next);
      })
      .catch(() => setQueues({}));
  }, [authLoading, session?.authenticated]);

  const subtitle = useMemo(() => {
    if (apiOk === false) {
      return t("common.apiUnreachable");
    }
    if (session?.authenticated && session.user?.username) {
      return `${session.user.username} · ${session.role ?? ""}`;
    }
    return t("home.welcomeHint");
  }, [apiOk, session, t]);

  return (
    <AppShell>
      <Card
        size="small"
        title={t("home.welcome")}
        extra={
          apiOk === false ? (
            <Text type="danger">{t("common.apiUnreachable")}</Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {subtitle}
            </Text>
          )
        }
      >
        <HomeHeroBanner />
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          {t("home.welcomeHint")}
        </Paragraph>
        <HomeTileGrid queues={queues} />
      </Card>
      <PageGuide guideKey="home" />
    </AppShell>
  );
}
